import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Flex,
  Grid,
  Image,
  SimpleGrid,
  Box,
  useColorModeValue,
  VStack,
  Text,
  Button,
  Badge,
} from "@chakra-ui/react";
import {
  CartIcon,
  DocumentIcon,
  GlobeIcon,
  WalletIcon,
} from "components/Icons/Icons.js";
import { FaBell, FaCreditCard } from "react-icons/fa";
import { FiBook } from "react-icons/fi";
import LineChart from "components/Charts/LineChart";
import peopleImage from "assets/img/people-image.png";
import { useAppContext } from "../../../context/appContext";
import { withModuleCheck, useModuleCheck, MODULES, useUserAuth, canUserAccessCategory } from "utils/site";
import {
  getReportCustomers,
  getReportOrders,
  getOrders,
  getTicketsSold,
} from "services/woo";
import { getPaymentStats } from "services/billing";
import { getAllSurveyResponses, getAllFiles } from "services/surveyService";
import EventDashboard from "./components/EventDashboard";
import ActiveUsers from "./components/ActiveUsers";
import BuiltByDevelopers from "./components/BuiltByDevelopers";
import MiniStatistics from "./components/MiniStatistics";
import SurveysOverview from "./components/SurveysOverview";
import Projects from "./components/Projects";
import SalesOverview from "./components/SalesOverview";
import WorkWithTheRockets from "./components/WorkWithTheRockets";
import { getSites } from "../../../services/userService";
import { getSiteConfig, saveSiteConfig } from "../../../utils/site";
import DynamicPaginator from "../../../components/Tables/DynamicPaginator";
import { InfoCard } from "components/Card/EducationalCard";

const ITEMS_PER_PAGE = 10;

const iconSelector = (status) => ({
  pending: FaBell,
  completed: FaCreditCard,
  failed: FaCreditCard,
}[status] || FaBell);

const colorSelector = (status) => ({
  pending: "orange",
  completed: "teal.300",
  failed: "purple",
}[status] || "teal.300");

const titleSelector = (status, number) => ({
  pending: `Nueva orden #${number}`,
  completed: `Pago completado #${number}`,
  failed: `Pago fallido #${number}`,
  "survey-response": `${number}`,
}[status] || "Nueva actividad");

// Componentes con módulo — creados una sola vez fuera del componente
const TicketsComponent       = withModuleCheck(EventDashboard,    MODULES.TICKETS);
const SurveyComponent        = withModuleCheck(ActiveUsers,       MODULES.SURVEYS);
const SurveysOverviewComponent = withModuleCheck(SurveysOverview, MODULES.SURVEYS);
const ProjectsComponent      = withModuleCheck(Projects,          MODULES.PAYMENTS);

function Dashboard() {
  const iconBoxInside = useColorModeValue("white", "white");
  const { ordersGetParams } = useAppContext();

  // ── Módulos activos (hooks en nivel superior, no dentro de useMemo) ──────────
  const paymentsEnabled = useModuleCheck(MODULES.PAYMENTS);
  const ticketsEnabled  = useModuleCheck(MODULES.TICKETS);
  const surveysEnabled  = useModuleCheck(MODULES.SURVEYS);
  const filesEnabled    = useModuleCheck(MODULES.FILES);

  // ── Usuario autenticado ───────────────────────────────────────────────────────
  const { user: currentUser, loading: userLoading } = useUserAuth();
  const userRole = currentUser?.role || 'profesor';

  // ── Estado de datos ───────────────────────────────────────────────────────────
  const [reportSales,    setReportSales]    = useState({ total_sales: '' });
  const [reportCustomers, setReportCustomers] = useState({});
  const [reportOrders,   setReportOrders]   = useState({});
  const [getTimeLine,    setGetTimeLine]    = useState([]);
  const [getDashboardTableData, setDashboardTableData] = useState([]);
  const [allSurveys,     setAllSurveys]     = useState([]);
  const [allFiles,       setAllFiles]       = useState([]);
  const [totalTickets,   setTotalTickets]   = useState(0);
  const [totalOrders,    setTotalOrders]    = useState(0);
  const [isLoading,      setIsLoading]      = useState(false);
  const [error,          setError]          = useState(null);

  // ── Paginación y filtros ──────────────────────────────────────────────────────
  const [currentPage,  setCurrentPage]  = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [filters, setFilters] = useState([
    { name: 'startDate', type: 'date',   value: '',    placeholder: 'Fecha desde' },
    { name: 'endDate',   type: 'date',   value: '',    placeholder: 'Fecha hasta' },
    {
      name: 'status', type: 'select', value: 'all', placeholder: false,
      options: [
        { value: 'all',       label: 'Todos' },
        { value: 'completed', label: 'Pagado' },
        { value: 'used',      label: 'Escaneado' },
        { value: 'pending',   label: 'Pendiente' },
        { value: 'failed',    label: 'Fallido' },
      ],
    },
  ]);

  // Ref para distinguir carga inicial de cambios de filtro/página
  const mountedRef = useRef(false);

  // ── Handlers de filtros ───────────────────────────────────────────────────────
  const handleFilterChange = useCallback((name, value) => {
    setFilters(prev => prev.map(f => f.name === name ? { ...f, value } : f));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // ── fetchOrders ───────────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async (overrides = {}) => {
    if (!paymentsEnabled) return;
    setIsLoading(true);
    try {
      const statusFilter = filters.find(f => f.name === 'status')?.value;
      const startDate    = filters.find(f => f.name === 'startDate')?.value;
      const endDate      = filters.find(f => f.name === 'endDate')?.value;

      const params = {
        ...ordersGetParams,
        per_page: itemsPerPage,
        page: currentPage,
        ...(statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(startDate ? { after: startDate }  : {}),
        ...(endDate   ? { before: endDate }   : {}),
        ...overrides,
      };

      const response = await getOrders(params);
      const orderFormatted = response.data.map(order => ({
        logo: DocumentIcon,
        username: order?.customer_info?.username,
        group: order?.customer_info?.meta_data?.find(m => m.key === 'group_name')?.value,
        name: `${order?.customer_info?.first_name} ${order?.customer_info?.last_name}`,
        mes: order?.meta_data?.find(m => m.key === 'mes')?.value,
        ...order,
      }));

      setDashboardTableData(orderFormatted);
      setTotalOrders(response.total);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setIsLoading(false);
    }
  }, [paymentsEnabled, ordersGetParams, itemsPerPage, currentPage, filters]);

  // ── fetchReports ──────────────────────────────────────────────────────────────
  const fetchReports = useCallback(async () => {
    const promises = [];
    const results  = {};

    if (paymentsEnabled) {
      promises.push(
        getReportCustomers().then(d => { results.customers    = d; }),
        getPaymentStats().then(d    => { results.paymentStats = d; }),
        getReportOrders().then(d    => { results.orders       = d; }),
      );
    }
    if (ticketsEnabled) {
      promises.push(getTicketsSold().then(d => { results.tickets = d; }));
    }

    if (!promises.length) return;

    try {
      await Promise.all(promises);

      if (results.customers) {
        const paying    = results.customers.find(c => c.slug === 'paying');
        const notPaying = results.customers.find(c => c.slug === 'non_paying');
        setReportCustomers({
          customersPaying: paying,
          customersNotPaying: notPaying,
          customersTotal: (paying?.total || 0) + (notPaying?.total || 0),
        });
      }
      if (results.paymentStats) {
        setReportSales({
          'wc-completed': { total: results.paymentStats.total_collected || 0, count: results.paymentStats.total_orders || 0 },
          'wc-pending':   { total: results.paymentStats.pending_amount  || 0, count: results.paymentStats.pending_orders || 0 },
          total_sales: '$' + (results.paymentStats.total_collected || 0),
        });
      }
      if (results.orders) {
        setReportOrders({
          ordersPending:   results.orders.find(o => o.slug === 'pending'),
          ordersCompleted: results.orders.find(o => o.slug === 'completed'),
          ordersUsed:      results.orders.find(o => o.slug === 'used'),
        });
      }
      if (results.tickets) {
        setTotalTickets(results.tickets.totals?.sold || 0);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Error al cargar los reportes');
    }
  }, [paymentsEnabled, ticketsEnabled]);

  // ── fetchSurveys ──────────────────────────────────────────────────────────────
  const fetchSurveys = useCallback(async () => {
    if (!surveysEnabled || userLoading) return;
    try {
      const res = await getAllSurveyResponses();
      if (!res?.data?.data) return;

      const filtered = res.data.data.filter(r =>
        canUserAccessCategory(userRole, r.survey?.category || 'otros')
      );

      setAllSurveys({ ...res.data, data: filtered, total: filtered.length });
      setGetTimeLine(filtered.map(r => ({
        logo: iconSelector(r.status),
        title: titleSelector('survey-response', `#${r.id} ${r.survey.name}`),
        date: r.created_at,
        color: colorSelector(r.status),
        type: 'survey',
        ...r,
      })));
    } catch (err) {
      console.error('Error fetching surveys:', err);
    }
  }, [surveysEnabled, userRole, userLoading]);

  // ── fetchFiles ────────────────────────────────────────────────────────────────
  const fetchFiles = useCallback(async () => {
    if (!filesEnabled) return;
    try {
      const files = await getAllFiles();
      setAllFiles(files);
    } catch (err) {
      console.error('Error fetching files:', err);
    }
  }, [filesEnabled]);

  // ── setConfiguration ──────────────────────────────────────────────────────────
  const setConfiguration = useCallback(async () => {
    try {
      const config = JSON.parse(getSiteConfig());
      const res    = await getSites();
      if (!res.success || !Array.isArray(res.data)) return;

      const updated = res.data.find(s => s.id === config?.id);
      if (updated) saveSiteConfig(JSON.stringify(updated));
    } catch (err) {
      console.error('Error en setConfiguration:', err);
    }
  }, []);

  // ── Carga inicial: UN solo efecto ─────────────────────────────────────────────
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const load = async () => {
      await setConfiguration();

      const promises = [];
      if (paymentsEnabled) { promises.push(fetchReports(), fetchOrders()); }
      if (surveysEnabled && !userLoading) { promises.push(fetchSurveys()); }
      if (filesEnabled)    { promises.push(fetchFiles()); }

      await Promise.all(promises);
    };

    load();
  }, []); // Solo en mount

  // ── Surveys: re-fetch cuando el usuario esté listo (si llegó tarde) ───────────
  useEffect(() => {
    if (!mountedRef.current || userLoading || !surveysEnabled) return;
    fetchSurveys();
  }, [userLoading]); // solo reacciona al cambio de userLoading

  // ── Orders: re-fetch cuando cambian filtros o página (no en mount) ────────────
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) { isFirstFilterRender.current = false; return; }
    fetchOrders();
  }, [currentPage, itemsPerPage, filters]);

  // ── Stats memoizadas ──────────────────────────────────────────────────────────
  const moduleStats = useMemo(() => [
    {
      module: MODULES.PAYMENTS,
      enabled: paymentsEnabled,
      stats: [
        {
          title: "Estudiantes Activos",
          amount: reportCustomers.customersTotal || 0,
          icon: GlobeIcon, percentage: 5, trend: 'up', color: 'primary',
          subtitle: "Estudiantes registrados",
        },
        {
          title: "Mensualidades al Día",
          amount: (reportOrders.ordersCompleted?.total || 0) + (reportOrders.ordersUsed?.total || 0),
          icon: CartIcon, percentage: 8, trend: 'up', color: 'secondary',
          subtitle: "Pagos completados",
        },
        {
          title: "Mensualidades Pendientes",
          amount: reportOrders.ordersPending?.total || 0,
          icon: DocumentIcon, percentage: 14, trend: 'down', color: 'accent',
          subtitle: "Requieren seguimiento",
        },
      ],
    },
    {
      module: MODULES.SURVEYS,
      enabled: surveysEnabled,
      stats: [{
        title: "Formularios Académicos",
        amount: allSurveys?.total || 0,
        icon: DocumentIcon, percentage: 0, trend: 'up', color: 'primary',
        subtitle: "Formularios completados",
      }],
    },
    {
      module: MODULES.TICKETS,
      enabled: ticketsEnabled,
      stats: [{
        title: "Eventos Académicos",
        amount: totalTickets,
        icon: CartIcon, percentage: 0, trend: 'up', color: 'secondary',
        subtitle: "Participación en eventos",
      }],
    },
    {
      module: MODULES.FILES,
      enabled: filesEnabled,
      stats: [{
        title: "Documentos Académicos",
        amount: allFiles?.data?.total || 0,
        icon: DocumentIcon, percentage: 0, trend: 'up', color: 'accent',
        subtitle: "Documentos almacenados",
      }],
    },
  ], [
    paymentsEnabled, surveysEnabled, ticketsEnabled, filesEnabled,
    reportCustomers.customersTotal,
    reportOrders.ordersCompleted?.total, reportOrders.ordersUsed?.total, reportOrders.ordersPending?.total,
    allSurveys?.total, totalTickets, allFiles?.data?.total,
  ]);

  if (error) {
    return (
      <Box p={4} bg="red.50" color="red.500" borderRadius="md">{error}</Box>
    );
  }

  return (
    <Flex flexDirection='column' pt={{ base: "120px", md: "75px" }}>
      {/* Statistics */}
      <Box mb={8}>
        <VStack spacing={6} align="stretch">
          <InfoCard title="Panel de Control Educativo" icon={FiBook} variant="highlighted">
            <Text color="gray.600">
              Gestiona y supervisa el rendimiento académico y administrativo de tu institución educativa
            </Text>
          </InfoCard>

          <SimpleGrid columns={{ sm: 1, md: 2, lg: 4 }} spacing={6}>
            {moduleStats.map(({ module, enabled, stats }) =>
              enabled && stats.map((stat, index) => (
                <MiniStatistics
                  key={`${module}-${index}`}
                  title={stat.title}
                  amount={stat.amount}
                  percentage={stat.percentage}
                  trend={stat.trend}
                  color={stat.color}
                  subtitle={stat.subtitle}
                  icon={<stat.icon h={"24px"} w={"24px"} color={iconBoxInside} />}
                />
              ))
            )}
          </SimpleGrid>
        </VStack>
      </Box>

      {/* Banner tickets */}
      {ticketsEnabled && (
        <Grid
          templateColumns={{ md: "1fr", lg: "1.8fr 1.2fr" }}
          templateRows={{ md: "1fr auto", lg: "1fr" }}
          mb='24px' gap='24px'>
          <BuiltByDevelopers
            title={"Eventos Académicos"}
            name={"ACTIVIDADES EDUCATIVAS"}
            description={"Gestión de eventos, conferencias y actividades extracurriculares"}
            image={
              <Image
                src={'https://ticketa.cl/wp-content/uploads/2024/11/ticketa-logo-white.png'}
                alt='Imagen' minWidth={{ md: "300px", lg: "auto" }}
              />
            }
          />
          <WorkWithTheRockets
            backgroundImage={peopleImage}
            title={"Comunidad Educativa Conectada"}
            description={"Facilita la participación estudiantil en eventos académicos, talleres y actividades extracurriculares de forma sencilla."}
          />
        </Grid>
      )}

      {/* Analytics */}
      <Grid
        templateColumns={{ sm: "1fr", lg: ticketsEnabled ? "1.5fr 1fr" : "1fr" }}
        gap='24px' mb='24px'>
        {ticketsEnabled ? (
          <>
            <InfoCard title="Gestión de Eventos Académicos" icon={FiBook} variant="highlighted">
              <TicketsComponent categoryId={'tickets'} />
            </InfoCard>
            <VStack spacing={6} align="stretch">
              {paymentsEnabled && (
                <InfoCard title="Ingresos Académicos" icon={WalletIcon}>
                  <SalesOverview title={null} percentage={5} chart={<LineChart />} />
                </InfoCard>
              )}
              {surveysEnabled && (
                <InfoCard title="Participación en Formularios" icon={DocumentIcon}>
                  <SurveyComponent title={null} percentage={23} />
                </InfoCard>
              )}
            </VStack>
          </>
        ) : (
          <VStack spacing={6} align="stretch">
            {paymentsEnabled && (
              <InfoCard title="Ingresos por Mensualidades" icon={WalletIcon} variant="highlighted">
                <SalesOverview title={null} percentage={5} chart={<LineChart />} />
              </InfoCard>
            )}
            {surveysEnabled && (
              <InfoCard title="Actividad de Formularios Académicos" icon={DocumentIcon} variant="highlighted">
                <SurveyComponent title={null} percentage={23} />
              </InfoCard>
            )}
          </VStack>
        )}
      </Grid>

      {/* Transacciones y formularios recientes */}
      <Grid
        templateColumns={{
          sm: "1fr",
          lg: (paymentsEnabled && surveysEnabled) ? "2fr 1fr" : "1fr",
        }}
        gap='24px' mb='24px'>
        {paymentsEnabled && (
          <InfoCard
            title="Transacciones Académicas Recientes"
            icon={FaBell}
            action={<Button size="sm" variant="educational-ghost">Ver Todas</Button>}
          >
            <DynamicPaginator
              filters={filters}
              onFilterChange={handleFilterChange}
              totalItems={totalOrders}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              isLoading={isLoading}
            >
              <ProjectsComponent
                title={null}
                captions={["Estudiante", "Concepto", "Monto", "Estado", "Fecha"]}
                data={getDashboardTableData}
                isLoading={isLoading}
              />
            </DynamicPaginator>
          </InfoCard>
        )}
        {surveysEnabled && (
          <InfoCard
            title="Actividad de Formularios Académicos"
            icon={DocumentIcon}
            action={<Badge colorScheme="primary" borderRadius="full">{allSurveys?.total || 0}</Badge>}
          >
            <SurveysOverviewComponent
              title={null}
              amount={allSurveys?.total || 0}
              data={getTimeLine}
            />
          </InfoCard>
        )}
      </Grid>
    </Flex>
  );
}

export default Dashboard;
